import { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { CASES } from '@/data/brands';
import { GripVertical, ArrowRight, ArrowLeft, ArrowUpRight } from 'lucide-react';

function BeforeAfterSlider({ before, after, isAr, labels }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const draggingRef = useRef(false);

  const move = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    p = Math.max(0, Math.min(100, p));
    setPos(p);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      move(x);
    };
    const onUp = () => (draggingRef.current = false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
  }, [move]);

  const onDown = (e) => {
    draggingRef.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    move(x);
  };

  return (
    <motion.div
      ref={ref}
      className="dwk-slider select-none"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      onMouseDown={onDown}
      onTouchStart={onDown}
      data-testid="ba-slider"
    >
      {/* Background = Before (visible on the right by default) */}
      <img src={before} alt={labels.before} loading="lazy" />
      {/* Clipped layer = After (revealed on the left as the handle moves) */}
      <div className="after-clip" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={after} alt={labels.after} loading="lazy" />
      </div>

      {/* Labels — قبل (Before) on the left, بعد (After) on the right */}
      <div className="absolute top-4 right-4 rounded-full bg-black/45 backdrop-blur text-white text-[11px] tracking-[0.28em] uppercase px-3 py-1.5">
        {labels.after}
      </div>
      <div className="absolute top-4 left-4 rounded-full bg-black/45 backdrop-blur text-white text-[11px] tracking-[0.28em] uppercase px-3 py-1.5">
        {labels.before}
      </div>

      {/* Handle */}
      <div className="handle" style={{ left: `${pos}%` }}>
        <div className="knob" aria-label="Drag">
          <GripVertical size={20} />
        </div>
      </div>

      {/* Hint (fades out after first interaction) */}
      {pos > 48 && pos < 52 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/85 text-xs tracking-[0.35em] uppercase pointer-events-none">
          {labels.dragHint}
        </div>
      )}
    </motion.div>
  );
}

function PairPlaceholder({ label, isAr }) {
  return (
    <div
      className="w-full h-full flex items-center justify-center relative"
      style={{
        background:
          'radial-gradient(circle at 30% 30%, rgba(137,72,195,0.12), transparent 60%), radial-gradient(circle at 70% 70%, rgba(64,211,190,0.12), transparent 60%), #F7F8FC',
      }}
    >
      <div className="text-center px-3">
        <div className="text-[10px] md:text-[11px] tracking-[0.35em] uppercase text-[#8948C3]/60 font-en font-semibold">
          {label}
        </div>
        <div className={`mt-1 text-[10px] md:text-xs text-[#10092E]/35 ${isAr ? 'font-ar' : 'font-en'}`}>
          {isAr ? 'صورة قريبًا' : 'Photo coming soon'}
        </div>
      </div>
    </div>
  );
}

export default function Cases() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [showAll, setShowAll] = useState(false);
  const ArrowCta = isAr ? ArrowLeft : ArrowRight;

  const labels = {
    before: t('cases.before'),
    after: t('cases.after'),
    dragHint: `‹ ${t('cases.drag_hint')} ›`,
  };

  // Real case pairs — first entry uses the newly-uploaded Otesaly Botox forehead pair.
  // Each entry: { titleAr, titleEn, before, after }. Add more by pushing to this array.
  const GALLERY_CASES = [
    {
      titleAr: 'فلر ربلنجن ديب لشفاه أكثر جاذبية',
      titleEn: 'Replengen Deep Lip Filler',
      before: 'https://customer-assets-cm19k8pv.emergentagent.net/job_premium-medica/artifacts/hqeqcqmv_%D8%B1%D8%A8%D9%84%D9%86%D8%AD%D9%86%20%D8%B4%D9%81%D9%87%20%D9%82%D8%A8%D9%841.jpg',
      after:  'https://customer-assets-cm19k8pv.emergentagent.net/job_premium-medica/artifacts/oaegypxi_%D8%B1%D8%A8%D9%84%D9%86%D8%AD%D9%86%20%D8%B4%D9%81%D9%87%20%D8%A8%D8%B9%D8%AF1.jpg',
    },
    {
      titleAr: 'بوتكس أوتسالي الفرنسي',
      titleEn: 'Otesaly French Botox',
      before: 'https://customer-assets-cm19k8pv.emergentagent.net/job_premium-medica/artifacts/k03qgtnj_%D8%A8%D9%88%D8%AA%D9%83%D8%B3%20%D9%82%D8%A8%D9%841.jpg',
      after:  'https://customer-assets-cm19k8pv.emergentagent.net/job_premium-medica/artifacts/36oeb4te_%D8%A8%D9%88%D8%AA%D9%83%D8%B3%20%D8%A8%D8%B9%D8%AF1.jpg',
    },
    {
      titleAr: 'فلر ربلنجن ديب لشفاه أكثر جاذبية',
      titleEn: 'Replengen Deep — For More Attractive Lips',
      before: 'https://customer-assets-cm19k8pv.emergentagent.net/job_premium-medica/artifacts/8tepg68x_%D8%B1%D8%A8%D9%84%D9%86%D8%AD%D9%86%20%D8%B4%D9%81%D9%87%20%D9%82%D8%A8%D9%842.jpg',
      after:  'https://customer-assets-cm19k8pv.emergentagent.net/job_premium-medica/artifacts/3qrhd7a4_%D8%B1%D8%A8%D9%84%D9%86%D8%AD%D9%86%20%D8%B4%D9%81%D9%87%20%D8%A8%D8%B9%D8%AF2_.jpg',
    },
    {
      titleAr: 'ربلنجن إمبلانت اس',
      titleEn: 'Replengen Implant S',
      before: 'https://customer-assets-cm19k8pv.emergentagent.net/job_premium-medica/artifacts/7gmsj85m_%D8%AA%D9%83%D8%B3%D8%A7%D8%B3%20%20%D9%82%D8%A8%D9%84.jpg',
      after:  'https://customer-assets-cm19k8pv.emergentagent.net/job_premium-medica/artifacts/4we07g0i_%D8%AA%D9%83%D8%B3%D8%A7%D8%B3%20%D8%A8%D8%B9%D8%AF.jpg',
    },
    {
      titleAr: 'فلر ربلنجن ديب لشفاه أكثر جاذبية',
      titleEn: 'Replengen Deep — For More Attractive Lips',
      before: 'https://customer-assets-cm19k8pv.emergentagent.net/job_premium-medica/artifacts/wqbl521d_%D8%B1%D8%A8%D9%84%D9%86%D8%AD%D9%86%20%D8%B4%D9%81%D9%87%20%D9%823_.jpg',
      after:  'https://customer-assets-cm19k8pv.emergentagent.net/job_premium-medica/artifacts/jry86b7j_%D8%B1%D8%A8%D9%84%D9%86%D8%AD%D9%86%20%D8%B4%D9%81%D9%87%20%D8%A8%D8%B9%D8%AF3_.jpg',
    },
    {
      titleAr: 'فلر شفاه أوتسالي DEEP',
      titleEn: 'Otesaly Deep Lip Filler',
      before: 'https://customer-assets-cm19k8pv.emergentagent.net/job_premium-medica/artifacts/xon6ga1e_%D8%B4%D9%81%D9%87%20%D8%A7%D9%88%D8%B3%D8%AA%D8%A7%D9%84%D9%8A%20%D9%82%D8%A8%D9%84.jpg',
      after:  'https://customer-assets-cm19k8pv.emergentagent.net/job_premium-medica/artifacts/0p29efyf_%D8%B4%D9%81%D9%87%20%D8%A7%D9%88%D8%B3%D8%AA%D8%A7%D9%84%D9%8A%20%D8%A8%D8%B9%D8%AF.jpg',
    },
    {
      titleAr: 'نتائج بوتكس أوتسالي الفرنسي',
      titleEn: 'Otesaly French Botox Results',
      single: 'https://customer-assets-cm19k8pv.emergentagent.net/job_premium-medica/artifacts/pi25nhcx_%D8%A8%D9%88%D8%AA%D9%83%D8%B3%201.jpg',
    },
    {
      titleAr: 'نتائج بوتكس أوتسالي الفرنسي',
      titleEn: 'Otesaly French Botox Results',
      single: 'https://customer-assets-cm19k8pv.emergentagent.net/job_premium-medica/artifacts/e8p1b33t_%D8%A8%D9%88%D8%AA%D9%83%D8%B3%202.jpg',
    },
  ];

  return (
    <section
      id="cases"
      data-testid="cases-section"
      className="relative bg-white py-24 md:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-8">
            <div className="text-[14px] md:text-[15px] tracking-[0.32em] uppercase text-[#8948C3] mb-6 font-semibold" data-testid="cases-eyebrow">
              {t('cases.eyebrow')}
            </div>
            <h2
              className={`text-[#10092E] font-bold tracking-tight leading-[1.05] ${
                isAr ? 'font-ar text-5xl md:text-7xl' : 'font-en text-5xl md:text-7xl'
              }`}
              data-testid="cases-heading"
            >
              {t('cases.heading')}
            </h2>
          </div>
          <div className={`md:col-span-4 text-[#10092E]/60 ${isAr ? 'font-ar text-base md:text-lg' : 'font-en text-base md:text-lg'}`}>
            {t('cases.subheading')}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
          {CASES.slice(0, 3).map((c, i) => (
            <div key={c.id} data-testid={`case-${i + 1}`}>
              <BeforeAfterSlider before={c.before} after={c.after} isAr={isAr} labels={labels} />
            </div>
          ))}
        </div>

        {/* Show more button */}
        <div className="mt-14 flex justify-center">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            data-testid="cases-show-more"
            className={`inline-flex items-center gap-3 rounded-full px-7 md:px-9 py-3.5 md:py-4 text-sm md:text-base font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-15px_rgba(137,72,195,0.55)] ${isAr ? 'font-ar' : 'font-en'}`}
            style={{ background: 'linear-gradient(135deg, #8948C3 0%, #005CFF 60%, #40D3BE 100%)' }}
          >
            {showAll
              ? (isAr ? 'إخفاء المعرض' : 'Hide gallery')
              : (isAr ? 'اضغط لمشاهدة المزيد من الصور' : 'Click to view more photos')}
            {showAll ? null : <ArrowCta size={16} />}
          </button>
        </div>

        {/* Expandable square gallery (placeholders until real images arrive) */}
        <AnimatePresence initial={false}>
          {showAll && (
            <motion.div
              key="gallery"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
              data-testid="cases-gallery"
            >
              <div className="pt-12 flex flex-wrap justify-center gap-5 md:gap-6" dir={isAr ? 'rtl' : 'ltr'}>
                {GALLERY_CASES.map((pair, i) => {
                  const title = isAr ? pair.titleAr : pair.titleEn;
                  return (
                    <motion.article
                      key={i}
                      initial={{ opacity: 0, y: 24 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.55, delay: i * 0.06 }}
                      className={`w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] group rounded-2xl overflow-hidden border border-[#10092E]/10 bg-white shadow-[0_10px_40px_-25px_rgba(16,9,46,0.35)] hover:shadow-[0_20px_60px_-25px_rgba(137,72,195,0.35)] transition-shadow`}
                      data-testid={`gallery-pair-${i + 1}`}
                    >
                      {pair.single ? (
                        /* Single-image case — the full before/after is baked into one visual */
                        <div className="relative aspect-square bg-[#F7F8FC] overflow-hidden">
                          <img
                            src={pair.single}
                            alt={title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                            loading="lazy"
                          />
                          <span className="absolute top-2.5 left-2.5 text-[9px] md:text-[10px] tracking-[0.28em] font-semibold text-white bg-[#10092E]/70 backdrop-blur px-2 py-1 rounded-full font-en">
                            {isAr ? 'قبل' : 'BEFORE'}
                          </span>
                          <span className="absolute bottom-2.5 left-2.5 text-[9px] md:text-[10px] tracking-[0.28em] font-semibold text-white bg-[#8948C3]/85 backdrop-blur px-2 py-1 rounded-full font-en">
                            {isAr ? 'بعد' : 'AFTER'}
                          </span>
                        </div>
                      ) : (
                      /* Pair — BEFORE | ARROW | AFTER (LTR order regardless of language,
                          so the visual reading is always before→after) */
                      <div className="relative grid grid-cols-2" dir="ltr">
                        {/* BEFORE */}
                        <div className="relative aspect-square bg-[#F7F8FC] overflow-hidden">
                          {pair.before ? (
                            <img
                              src={pair.before}
                              alt={`${title} — before`}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                              loading="lazy"
                            />
                          ) : (
                            <PairPlaceholder label="BEFORE" isAr={isAr} />
                          )}
                          <span className="absolute top-2.5 left-2.5 text-[9px] md:text-[10px] tracking-[0.28em] font-semibold text-white bg-[#10092E]/70 backdrop-blur px-2 py-1 rounded-full font-en">
                            {isAr ? 'قبل' : 'BEFORE'}
                          </span>
                        </div>

                        {/* AFTER */}
                        <div className="relative aspect-square bg-[#F7F8FC] overflow-hidden">
                          {pair.after ? (
                            <img
                              src={pair.after}
                              alt={`${title} — after`}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                              loading="lazy"
                            />
                          ) : (
                            <PairPlaceholder label="AFTER" isAr={isAr} />
                          )}
                          <span className="absolute top-2.5 right-2.5 text-[9px] md:text-[10px] tracking-[0.28em] font-semibold text-white bg-[#8948C3]/85 backdrop-blur px-2 py-1 rounded-full font-en">
                            {isAr ? 'بعد' : 'AFTER'}
                          </span>
                        </div>

                        {/* Subtle divider + arrow badge — sits between the two images */}
                        <div className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-transparent via-[#10092E]/15 to-transparent" />
                        <div
                          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full grid place-items-center shadow-[0_8px_24px_-6px_rgba(137,72,195,0.5)]"
                          style={{ background: 'linear-gradient(135deg, #8948C3 0%, #40D3BE 100%)' }}
                        >
                          <ArrowRight size={14} className="text-white" />
                        </div>
                      </div>
                      )}

                      {/* Case title */}
                      <div className={`px-4 md:px-5 py-3.5 md:py-4 flex items-center justify-between gap-3 ${isAr ? 'flex-row-reverse' : ''}`}>
                        <div className="min-w-0">
                          <div className="text-[9px] md:text-[10px] tracking-[0.35em] uppercase text-[#8948C3]/75 font-en mb-0.5">
                            {String(i + 1).padStart(2, '0')} · {isAr ? 'حالة' : 'Case'}
                          </div>
                          <div className={`text-sm md:text-[15px] font-semibold text-[#10092E] truncate ${isAr ? 'font-ar text-right' : 'font-en text-left'}`}>
                            {title}
                          </div>
                        </div>
                        <ArrowUpRight
                          size={16}
                          className="shrink-0 text-[#10092E]/25 group-hover:text-[#8948C3] transition-colors"
                        />
                      </div>
                    </motion.article>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <p
          className={`mt-10 text-xs md:text-sm text-[#10092E]/50 max-w-3xl ${isAr ? 'font-ar' : 'font-en'}`}
          data-testid="cases-disclaimer"
        >
          {t('cases.disclaimer')}
        </p>
      </div>
    </section>
  );
}
